import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ticketingDashboardRequest } from "../utility/ticketingDashboard";
const EMPTY_PARTICIPANTS = [];
const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
export function participantRow(person) {
  return {
    ...person,
    lightweight: true,
    details: [
      { name: "Firstname", value: person.firstName || "" },
      { name: "Name", value: person.lastName || "" },
      { name: "Email", value: person.email || "" },
    ],
  };
}
export default function useTicketingDashboardData({
  eventId,
  orgId,
  visible = false,
  sort = "creationDate",
  search = "",
}) {
  const selection = `${eventId}:${orgId}`,
    scope = useRef(selection);
  scope.current = selection;
  const summaryRun = useRef(0),
    listRun = useRef(0),
    summaryBusy = useRef(null),
    listBusy = useRef(null);
  const cache = useRef({ scope: null, items: [], cursor: null, complete: false });
  const [summary, setSummary] = useState(null),
    [summaryScope, setSummaryScope] = useState(null),
    [summaryLoading, setSummaryLoading] = useState(true),
    [summaryError, setSummaryError] = useState(false),
    [summaryErrorStatus, setSummaryErrorStatus] = useState(null);
  const [items, setItems] = useState([]),
    [listLoading, setListLoading] = useState(false),
    [listError, setListError] = useState(false),
    [listReady, setListReady] = useState(false),
    [loadedCount, setLoadedCount] = useState(0),
    [page, setPage] = useState(0),
    [debouncedSearch, setDebouncedSearch] = useState(search);
  const loadSummary = useCallback(async () => {
    if (!eventId || !orgId || summaryBusy.current === selection) return;
    const run = ++summaryRun.current;
    summaryBusy.current = selection;
    setSummaryLoading(true);
    try {
      const result = await ticketingDashboardRequest("summary", { eventId, orgId });
      if (scope.current !== selection || run !== summaryRun.current) return;
      setSummary(result);
      setSummaryScope(selection);
      setSummaryError(false);
      setSummaryErrorStatus(null);
    } catch (error) {
      if (scope.current === selection && run === summaryRun.current) {
        setSummaryError(true);
        setSummaryErrorStatus(error.status || null);
      }
    } finally {
      if (scope.current === selection && run === summaryRun.current) {
        summaryBusy.current = null;
        setSummaryLoading(false);
      }
    }
  }, [eventId, orgId, selection]);
  const loadParticipants = useCallback(
    async (force = false) => {
      if (!eventId || !orgId || (listBusy.current === selection && !force)) return;
      if (cache.current.scope === selection && cache.current.complete && !force) return;
      if (force || cache.current.scope !== selection)
        cache.current = { scope: selection, items: [], cursor: null, complete: false };
      const current = cache.current,
        run = ++listRun.current;
      listBusy.current = selection;
      setListLoading(true);
      setListError(false);
      setListReady(false);
      setLoadedCount(current.items.length);
      try {
        for (;;) {
          const result = await ticketingDashboardRequest("participants", {
            eventId,
            orgId,
            cursor: current.cursor,
          });
          if (scope.current !== selection || run !== listRun.current) return;
          current.items.push(...result.items);
          current.cursor = result.nextCursor;
          setLoadedCount(current.items.length);
          if (!result.nextCursor) {
            current.complete = true;
            setItems(current.items);
            setListReady(true);
            break;
          }
        }
      } catch {
        if (scope.current === selection && run === listRun.current) setListError(true);
      } finally {
        if (scope.current === selection && run === listRun.current) {
          listBusy.current = null;
          setListLoading(false);
        }
      }
    },
    [eventId, orgId, selection],
  );
  const invalidateRequests = useCallback(() => {
    summaryRun.current++;
    listRun.current++;
    summaryBusy.current = null;
    listBusy.current = null;
    cache.current = { scope: null, items: [], cursor: null, complete: false };
  }, []);
  useEffect(() => {
    setSummary(null);
    setSummaryScope(null);
    setSummaryError(false);
    setSummaryErrorStatus(null);
    setSummaryLoading(true);
    cache.current = { scope: selection, items: [], cursor: null, complete: false };
    setItems([]);
    setListReady(false);
    setListError(false);
    setListLoading(false);
    setLoadedCount(0);
    setPage(0);
    loadSummary();
    return invalidateRequests;
  }, [selection, loadSummary, invalidateRequests]);
  useEffect(() => {
    if (visible) loadParticipants();
  }, [visible, loadParticipants]);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setPage(0), [sort, debouncedSearch]);
  const filtered = useMemo(() => {
    const text = normalize(debouncedSearch);
    const result = items.filter(
      (p) =>
        !text ||
        [p.firstName, p.lastName, `${p.firstName} ${p.lastName}`, p.email, p.id].some((v) =>
          normalize(v).includes(text),
        ),
    );
    return result.sort((a, b) => {
      let comparison =
        sort === "alphabetical"
          ? normalize(`${a.firstName} ${a.lastName}`).localeCompare(
              normalize(`${b.firstName} ${b.lastName}`),
            )
          : sort === "isRead"
            ? Number(b.isRead) - Number(a.isRead)
            : Number(a.creationDate) - Number(b.creationDate);
      return comparison || a.id.localeCompare(b.id);
    });
  }, [items, sort, debouncedSearch]);
  const activePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / 50) - 1));
  const updateParticipant = useCallback(
    async (id) => {
      if (!id) return;
      const selected = selection;
      try {
        const person = await ticketingDashboardRequest("participant", { eventId, orgId, id });
        if (scope.current !== selected) return;
        const details = Object.fromEntries((person.details || []).map((d) => [d.name, d.value]));
        const light = {
          id: person.id,
          firstName: details.Firstname || "",
          lastName: details.Name || "",
          email: details.Email || "",
          creationDate: Number(person.createdAt || 0),
          isRead: person.isRead === true,
          numberOfScan: Number(person.numberOfScan || 0),
          quantity: (person.tickets || []).reduce((n, t) => n + Number(t.number || 0), 0),
        };
        cache.current.items = cache.current.items.map((p) => (p.id === id ? light : p));
        setItems(cache.current.items);
      } catch (error) {
        if (scope.current !== selected) return;
        if (error.status === 404) {
          cache.current.items = cache.current.items.filter((p) => p.id !== id);
          setItems(cache.current.items);
        } else throw error;
      }
    },
    [selection, eventId, orgId],
  );
  const refresh = async (id) => {
    if (!id && !visible) {
      listRun.current++;
      listBusy.current = null;
      cache.current = { scope: selection, items: [], cursor: null, complete: false };
      setItems([]);
      setListReady(false);
      setListLoading(false);
      setLoadedCount(0);
    }
    await Promise.all([
      loadSummary(),
      id ? updateParticipant(id) : visible ? loadParticipants(true) : Promise.resolve(),
    ]);
  };
  return {
    summary: summaryScope === selection ? summary : null,
    ready: summaryScope === selection && summary?.status === "ready",
    summaryLoading,
    summaryError,
    summaryErrorStatus,
    participants:
      listReady && cache.current.scope === selection
        ? filtered.slice(activePage * 50, (activePage + 1) * 50).map(participantRow)
        : [],
    allParticipants: listReady && cache.current.scope === selection ? items : EMPTY_PARTICIPANTS,
    listLoading,
    listError,
    listReady,
    loadedCount,
    resultCount: filtered.length,
    page: activePage,
    nextCursor: (activePage + 1) * 50 < filtered.length ? "local" : null,
    nextPage: () => setPage(activePage + 1),
    previousPage: () => setPage(Math.max(0, activePage - 1)),
    loadSummary,
    loadParticipants,
    refresh,
    updateParticipant,
  };
}
